// Touch keypad entry for the converter: digits, a decimal point, a cooking
// fraction (¼ ½ ¾ ⅓ ⅔), sign and backspace. Kept pure so it is unit-tested.

export type FracKey = "1/4" | "1/3" | "1/2" | "2/3" | "3/4";

export const FRACTION_KEYS: { key: FracKey; label: string; value: number }[] = [
  { key: "1/4", label: "¼", value: 1 / 4 },
  { key: "1/3", label: "⅓", value: 1 / 3 },
  { key: "1/2", label: "½", value: 1 / 2 },
  { key: "2/3", label: "⅔", value: 2 / 3 },
  { key: "3/4", label: "¾", value: 3 / 4 },
];

export interface KeypadState {
  /** Whole/decimal part as typed: "", "12", "0.5". */
  digits: string;
  frac: FracKey | null;
  negative: boolean;
}

export type KeypadKey = "0" | "1" | "2" | "3" | "4" | "5" | "6" | "7" | "8" | "9" | "." | "back" | "clear" | "sign" | FracKey;

export const EMPTY_KEYPAD: KeypadState = { digits: "", frac: null, negative: false };
const MAX_DIGITS = 7;

export function keypadPress(s: KeypadState, key: KeypadKey): KeypadState {
  switch (key) {
    case "clear":
      return EMPTY_KEYPAD;
    case "back":
      if (s.frac) return { ...s, frac: null };
      if (s.digits) return { ...s, digits: s.digits.slice(0, -1) };
      return { ...s, negative: false };
    case "sign":
      return { ...s, negative: !s.negative };
    case ".":
      if (s.frac || s.digits.includes(".") || s.digits.length >= MAX_DIGITS) return s;
      return { ...s, digits: (s.digits || "0") + "." };
    default:
      break;
  }
  if (key.includes("/")) {
    // Fractions pair with a whole number ("1 ½"), not with a decimal.
    if (s.digits.includes(".")) return s;
    return { ...s, frac: s.frac === key ? null : (key as FracKey) };
  }
  if (s.frac || s.digits.replace(".", "").length >= MAX_DIGITS) return s;
  if (s.digits === "0") return { ...s, digits: key };
  return { ...s, digits: s.digits + key };
}

export function keypadValue(s: KeypadState): number {
  const whole = s.digits && s.digits !== "." ? Number(s.digits) : 0;
  const frac = s.frac ? FRACTION_KEYS.find((f) => f.key === s.frac)!.value : 0;
  const v = (Number.isFinite(whole) ? whole : 0) + frac;
  return s.negative ? -v : v;
}

export function keypadText(s: KeypadState): string {
  const frac = s.frac ? FRACTION_KEYS.find((f) => f.key === s.frac)!.label : "";
  const body = s.digits && frac ? `${s.digits} ${frac}` : s.digits || frac || "0";
  return (s.negative ? "−" : "") + body;
}

/** Build keypad state from a number (after a stepper tap), preferring a cooking fraction when exact. */
export function keypadFrom(value: number): KeypadState {
  const negative = value < 0;
  const abs = Math.abs(value);
  const whole = Math.floor(abs + 1e-9);
  const rest = abs - whole;
  if (rest > 1e-6) {
    const f = FRACTION_KEYS.find((k) => Math.abs(k.value - rest) < 1e-6);
    if (f) return { digits: whole ? String(whole) : "", frac: f.key, negative };
  }
  const text = Number(abs.toFixed(3)).toString();
  return { digits: text === "0" ? "" : text, frac: null, negative: negative && abs > 0 };
}

/** Step a value by `step`, snapping to the step grid and never going below `min`. */
export function stepValue(value: number, step: number, dir: 1 | -1, min = 0): number {
  const snapped = dir > 0 ? Math.floor(value / step + 1e-9) * step + step : Math.ceil(value / step - 1e-9) * step - step;
  return Math.max(min, Number(snapped.toFixed(4)));
}
